import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import React, { useCallback, useEffect } from 'react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import ExpandLess from '@/assets/svgs/side-bar-icons/expand-less.icon.svg?react';
import ExpandMore from '@/assets/svgs/side-bar-icons/expand-more.icon.svg?react';
import ListItemButton from '@/shared/components/group-ui/side-menu/shared/components/styled-list-item';
import { StyledListItemIcon } from '@/shared/components/group-ui/side-menu/shared/components/styled-list-item-icon';
import { CustomMenuItemProps } from '@/shared/components/group-ui/side-menu/shared/types/types';
import { PUBLIC_PATH } from '@/shared/config/routes-config';
import { useLocalization } from '@/shared/hooks/useLocalization';

const normalizePath = (path: string): string => path.replace(/\/+$/, '') || '/';

/** Path relative to app routes (drops Vite base / PUBLIC_PATH if present). */
const routePathForMatch = (pathname: string): string => {
  let p = normalizePath(pathname);
  const base = normalizePath(PUBLIC_PATH || '');
  if (base && base !== '/' && (p === base || p.startsWith(`${base}/`))) {
    p = p === base ? '/' : normalizePath(p.slice(base.length));
  }
  return p === '' ? '/' : p;
};

const isPathSelected = (currentPath: string, itemPath: string): boolean =>
  itemPath.length > 0 &&
  itemPath !== '/' &&
  (currentPath === itemPath || currentPath.startsWith(`${itemPath}/`));

/** Pick the deepest matching FULL_PATH. Do not use `NAME`: many menu children share the same NAME (e.g. all "communication"). */
const getBestMatchedChildFullPath = (
  currentPath: string,
  items: Array<{ FULL_PATH?: string }>
): string | null => {
  let bestPath: string | null = null;
  let bestPathLength = -1;

  items.forEach((item) => {
    const itemPath = normalizePath(item.FULL_PATH || '');
    if (!itemPath || itemPath === '/') return;
    if (!isPathSelected(currentPath, itemPath)) return;
    if (itemPath.length > bestPathLength) {
      bestPathLength = itemPath.length;
      bestPath = itemPath;
    }
  });

  return bestPath;
};

const MultiLevel = (props: CustomMenuItemProps) => {
  const { item, setIsActive, isActive, setSelectedIndex, isCollapsed } = props;
  const { CHILDREN: children } = item;

  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;

  const { logo: Logo, activeLogo: ActiveLogo } = item.ICON;

  const theme = useTheme();
  const { isRTLDirect: isRTL } = useLocalization();

  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const currentPath = routePathForMatch(pathname);
    const shouldOpen = Boolean(
      children?.some((nestedItem) => {
        const itemPath = normalizePath(nestedItem.FULL_PATH || '');
        return itemPath.length > 0 && itemPath !== '/' && isPathSelected(currentPath, itemPath);
      })
    );
    setOpen(shouldOpen);
  }, [children, pathname]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isCollapsed) {
      setAnchorEl(event.currentTarget);
    } else {
      setOpen(!open);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleListItemClick = (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>,
      fullPath: string
    ) => {
    setSelectedIndex(fullPath);
    if (isCollapsed) {
      handlePopoverClose();
    }
  };

  const handleMouseEnter = useCallback(() => {
    setIsActive({ [item.NAME]: true });
  }, [item]);

  const handleMouseLeave = useCallback(() => {
    setIsActive({ [item.NAME]: false });
  }, [item]);

  const popoverOpen = Boolean(anchorEl);

  const menuItem = (
    <ListItemButton
      isRTL={isRTL}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        display: 'flex',
        alignItems: 'center !important',
        justifyContent: 'center !important'
      }}
    >
      <StyledListItemIcon>
        {isActive[item.NAME] || open ? <ActiveLogo /> : <Logo />}
      </StyledListItemIcon>
      {!isCollapsed && (
        <>
          <ListItemText primary={item.DISPLAY_NAME} />
          <Box sx={{ 
            transform: isRTL ? 'scaleX(-1)' : 'none',
            display: 'flex',
            alignItems: 'center'
          }}>
            {open ? <ExpandMore /> : <ExpandLess />}
          </Box>
        </>
      )}
    </ListItemButton>
  );

  const renderSubMenuItem = (nestedItem: any, key: number, selected: boolean) => {
    const content = (
      <ListItemButton
        nested
        isRTL={isRTL}
        key={key}
        onClick={(e) => {
          handleListItemClick(e, nestedItem.FULL_PATH || '');
          navigate(nestedItem.FULL_PATH);
        }}
        selected={selected}
        sx={{
          pl: isRTL ? 0 : 4,
          pr: isRTL ? 4 : 0
        }}
      >
        <ListItemText
          primary={nestedItem.DISPLAY_NAME}
          sx={{
            '& .MuiTypography-root': {
              fontSize: '0.875rem',
              fontWeight: selected ? 600 : 400
            }
          }}
        />
      </ListItemButton>
    );

    return isCollapsed ? (
      <Tooltip key={key} title={nestedItem.DISPLAY_NAME} placement={isRTL ? 'left' : 'right'}>
        {content}
      </Tooltip>
    ) : (
      content
    );
  };

  const currentPath = routePathForMatch(pathname);
  const bestMatchedChildPath = getBestMatchedChildFullPath(currentPath, children || []);

  const childrenList = (
    <List
      component="div"
      disablePadding
      sx={{
        '& .MuiListItemButton-root': {
          pl: isCollapsed ? 2 : (isRTL ? 0 : 4),
          pr: isCollapsed ? 0 : (isRTL ? 4 : 0)
        }
      }}
    >
      {children?.map((nestedItem, key) => {
        const itemPath = normalizePath(nestedItem.FULL_PATH || '');
        const selected =
          bestMatchedChildPath !== null &&
          itemPath.length > 0 &&
          itemPath === bestMatchedChildPath;

        return renderSubMenuItem(nestedItem, key, selected);
      })}
    </List>
  );

  return (
    <>
      {isCollapsed ? (
        <>
          <Tooltip title={item.DISPLAY_NAME} placement={isRTL ? 'left' : 'right'}>
            {menuItem}
          </Tooltip>
          <Popover
            open={popoverOpen}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: isRTL ? 'left' : 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: isRTL ? 'right' : 'left'
            }}
            PaperProps={{
              sx: {
                width: 200,
                ml: isRTL ? 0 : 1,
                mr: isRTL ? 1 : 0,
                '& .MuiList-root': {
                  p: 0
                }
              }
            }}
          >
            {childrenList}
          </Popover>
        </>
      ) : (
        <Box
          sx={{
            bgcolor: open ? theme.palette.action.active : theme.palette.common.white,
            borderRadius: theme.spacing(1),
            mb: theme.spacing(1)
          }}
        >
          {menuItem}
          <Collapse in={open} timeout="auto" unmountOnExit>
            {childrenList}
          </Collapse>
        </Box>
      )}
    </>
  );
};

export default MultiLevel;
